import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { PortalWidget } from "@entrilia/shared/api/portalWidgetsTypes";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import { Box, Button, FormControlLabel, FormHelperText, Stack, Switch, Typography } from "@mui/material";
import { useReducer, useState } from "react";
import { useNavigate } from "react-router";
import adminApi from "../../../../api/adminApi";
import type { InvestorPortalFeature } from "../../../../api/types/investorPortalTypes";
import type { ApprovalConfigurationVersion } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import { getRelevantWidgetsForPage } from "../widgets/portalWidgetsHelper";
import ContactsApprovalPanel from "./ContactsApprovalPanel";
import { getContactsApproval, includesContactsSelfService, shouldLockContactsEdit } from "./contactsApprovalHelper";
import PortalPageSettingsEdit from "./PortalPageSettingsEdit";
import { getInitialPagesSettingsState, reducer } from "./pagesSettingsState";

interface Props {
  initialEnabledFeatures: InvestorPortalFeature[];
  initialEnabledPreviewOnlyFeatures: InvestorPortalFeature[];
  widgets: PortalWidget[];
}

const updateInvestorPortalSettings = withErrorHandling(adminApi.updateInvestorPortalSettings);

const getContactsApprovalConfiguration = () => adminApi.getCurrentWorkflowApprovalConfiguration("MembersChangeRequest");

const PortalPagesSettingsEditor = ({ initialEnabledFeatures, initialEnabledPreviewOnlyFeatures, widgets }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { isPortalFeatureEnabled, hasPermissions, hasAnyPermission } = useClientContext();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(
    reducer,
    getInitialPagesSettingsState(initialEnabledFeatures, initialEnabledPreviewOnlyFeatures)
  );

  const [isSaving, setIsSaving] = useState(false);

  const [savedFeatures, setSavedFeatures] = useState({
    liveFeatures: initialEnabledFeatures,
    previewFeatures: initialEnabledPreviewOnlyFeatures,
  });

  const hasPermissionsToEdit = hasPermissions(["ManageInvestorPortalSettings"]);
  const canViewContactsApproval = hasAnyPermission(["ViewWorkflows", "ManageWorkflows"]);

  const [contactsApprovalResponse, contactsApprovalError, { setData: setContactsApprovalResponse }] = useFetch(
    getContactsApprovalConfiguration,
    { disabled: !hasPermissionsToEdit || !canViewContactsApproval }
  );

  const handleSaveClick = async () => {
    setIsSaving(true);

    const [resp, error] = await updateInvestorPortalSettings({
      enabledFeatures: state.liveFeatures,
      enabledPreviewOnlyFeatures: state.previewFeatures,
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[PortalPagesSettingsEditor] updateInvestorPortalSettings");
      sendNotificationError(error.message);
      return;
    }

    const updatedSavedFeatures = {
      liveFeatures: resp.settings.enabledFeatures ?? [],
      previewFeatures: resp.settings.enabledPreviewOnlyFeatures ?? [],
    };

    setSavedFeatures(updatedSavedFeatures);
    dispatch({ type: "RESET", payload: updatedSavedFeatures });
  };

  const handleCancelClick = () => {
    dispatch({
      type: "RESET",
      payload: savedFeatures,
    });
  };

  const newsEnabled = isPortalFeatureEnabled("InvestorPortalNewsEnabled");

  const portfolioHasNoEnabledWidgets = !getRelevantWidgetsForPage("PortfolioCompanies", widgets, newsEnabled).some(
    (w) => w.status === "Live"
  );

  const dashboardHasNoEnabledWidgets = !getRelevantWidgetsForPage("Dashboards", widgets, newsEnabled).some(
    (w) => w.status === "Live"
  );

  const isDirty =
    !arraysHaveSameItems(savedFeatures.liveFeatures, state.liveFeatures) ||
    !arraysHaveSameItems(savedFeatures.previewFeatures, state.previewFeatures);

  const isValid = state.liveFeatures.length > 0;

  if (contactsApprovalError) {
    logError(contactsApprovalError, "[PortalPagesSettingsEditor] getCurrentWorkflowApprovalConfiguration");
  }

  const contactsApproval = getContactsApproval(
    canViewContactsApproval,
    contactsApprovalResponse,
    contactsApprovalError
  );

  const isContactsSelfServiceSaved = includesContactsSelfService(savedFeatures);

  const isContactsEditLocked = shouldLockContactsEdit(
    contactsApproval,
    isContactsSelfServiceSaved,
    state.values.membershipEdit
  );

  const isAllPagesLiveLocked = !state.all && isContactsEditLocked;

  const allPagesLiveLockReason =
    contactsApproval.status === "WorkflowNotActive"
      ? "Activate the Contacts Change Request workflow first"
      : "Configure the Contacts approval workflow first";

  const handleContactsApprovalSaved = (configuration: ApprovalConfigurationVersion) => {
    setContactsApprovalResponse({ approvalConfiguration: configuration });
    if (configuration.steps.length === 0 && !isContactsSelfServiceSaved) {
      dispatch({ type: "TOGGLE_FIELD", payload: { membershipEdit: false } });
    }
  };

  return (
    <Stack spacing={3} sx={{ py: 2.5 }}>
      <Typography color="textSecondary">You can customize the availability of Investor portal pages.</Typography>
      <Box>
        <TooltipForDisabledAction
          title={
            isAllPagesLiveLocked
              ? `${allPagesLiveLockReason}: making all pages live also allows Investor Admins to submit contact change requests.`
              : undefined
          }
          isActionDisabled={isAllPagesLiveLocked}
        >
          <FormControlLabel
            disabled={!hasPermissionsToEdit || isAllPagesLiveLocked}
            control={<Switch checked={state.all} onChange={() => dispatch({ type: "TOGGLE_ALL" })} />}
            label={<Typography sx={{ ml: 0.5 }}>All pages live</Typography>}
          />
        </TooltipForDisabledAction>
      </Box>
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Typography variant="subtitle1">Portal Pages</Typography>
        <Stack spacing={1} sx={{ width: "100%" }}>
          <PortalPageSettingsEdit
            title="Dashboard"
            toggleValue={state.values.dashboards}
            onToggleChange={(newValue) => dispatch({ type: "TOGGLE_FIELD", payload: { dashboards: newValue } })}
            toggleDisabled={state.disabled.dashboards || !hasPermissionsToEdit}
            onEditClick={hasPermissionsToEdit ? () => navigate(pageRoutes.dashboardWidgetSettings) : undefined}
            showWidgetWarning={state.values.dashboards === "live" && dashboardHasNoEnabledWidgets}
          />
          {newsEnabled && (
            <PortalPageSettingsEdit
              title="News"
              toggleValue={state.values.news}
              onToggleChange={(newValue) => dispatch({ type: "TOGGLE_FIELD", payload: { news: newValue } })}
              toggleDisabled={state.disabled.news || !hasPermissionsToEdit}
            />
          )}
          <PortalPageSettingsEdit
            title="Inbox"
            toggleValue={state.values.inbox}
            onToggleChange={(newValue) => dispatch({ type: "TOGGLE_FIELD", payload: { inbox: newValue } })}
            toggleDisabled={state.disabled.inbox || !hasPermissionsToEdit}
          />
          <PortalPageSettingsEdit
            title="Portfolio"
            toggleValue={state.values.portfolioCompanies}
            onToggleChange={(newValue) => dispatch({ type: "TOGGLE_FIELD", payload: { portfolioCompanies: newValue } })}
            toggleDisabled={state.disabled.portfolioCompanies || !hasPermissionsToEdit}
            onEditClick={hasPermissionsToEdit ? () => navigate(pageRoutes.portfolioWidgetSettings) : undefined}
            showWidgetWarning={state.values.portfolioCompanies === "live" && portfolioHasNoEnabledWidgets}
          />
          <PortalPageSettingsEdit
            title="Fundraising"
            toggleValue={state.values.fundraising}
            onToggleChange={(newValue) => dispatch({ type: "TOGGLE_FIELD", payload: { fundraising: newValue } })}
            toggleDisabled={state.disabled.fundraising || !hasPermissionsToEdit}
          />
        </Stack>
      </Stack>
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Typography variant="subtitle1">Settings</Typography>
        <Stack spacing={1} sx={{ width: "100%" }}>
          <PortalPageSettingsEdit
            title="Investor Details"
            toggleValue={state.values.accountSettingsView}
            onToggleChange={(newValue) =>
              dispatch({ type: "TOGGLE_FIELD", payload: { accountSettingsView: newValue } })
            }
            toggleDisabled={state.disabled.accountSettingsView || !hasPermissionsToEdit}
            checkboxLabel="Enable self-service with approval"
            checkedValue={state.values.accountSettingsEdit}
            onCheckedChange={(checked) => dispatch({ type: "TOGGLE_FIELD", payload: { accountSettingsEdit: checked } })}
            checkboxDisabled={state.disabled.accountSettingsEdit || !hasPermissionsToEdit}
          />
          <PortalPageSettingsEdit
            title="Bank Account Details"
            toggleValue={state.values.bankAccountDetailsView}
            onToggleChange={(newValue) =>
              dispatch({ type: "TOGGLE_FIELD", payload: { bankAccountDetailsView: newValue } })
            }
            toggleDisabled={state.disabled.bankAccountDetailsView || !hasPermissionsToEdit}
            checkboxLabel="Enable self-service with approval"
            checkedValue={state.values.bankAccountDetailsEdit}
            onCheckedChange={(checked) =>
              dispatch({ type: "TOGGLE_FIELD", payload: { bankAccountDetailsEdit: checked } })
            }
            checkboxDisabled={state.disabled.bankAccountDetailsEdit || !hasPermissionsToEdit}
          />
          <PortalPageSettingsEdit
            title="Contacts"
            toggleValue={state.values.membership}
            onToggleChange={(newValue) => dispatch({ type: "TOGGLE_FIELD", payload: { membership: newValue } })}
            toggleDisabled={state.disabled.membership || !hasPermissionsToEdit}
            checkboxLabel="Enable self-service with approval"
            checkedValue={state.values.membershipEdit}
            onCheckedChange={(checked) => dispatch({ type: "TOGGLE_FIELD", payload: { membershipEdit: checked } })}
            checkboxDisabled={state.disabled.membershipEdit || !hasPermissionsToEdit || isContactsEditLocked}
          >
            {hasPermissionsToEdit && (
              <ContactsApprovalPanel
                contactsApproval={contactsApproval}
                onApprovalSaved={handleContactsApprovalSaved}
              />
            )}
          </PortalPageSettingsEdit>
        </Stack>

        {!isValid && <FormHelperText error>At least one page must be Live.</FormHelperText>}
      </Stack>
      {hasPermissionsToEdit && (
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={!isDirty || !isValid} loading={isSaving} onClick={handleSaveClick}>
            Save
          </Button>
          <Button variant="text" color="secondary" disabled={!isDirty} onClick={handleCancelClick}>
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default PortalPagesSettingsEditor;
